import { useRef, type ChangeEvent, type CSSProperties, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import {
  type SxProps,
  type Theme,
  Avatar,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Tooltip,
} from "@mui/material";
import InfoOutline from "@mui/icons-material/InfoOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { profileStore, snackStore } from "@/stores/Stores";
import { getAvatarUrl } from "@/util/avatarUtils";
import { processProfileImage, validateImageFile } from "@/util/imageUtils";
import getStyles from "./styles";

export const BasicInfo = observer(({ onSwitch2FA = () => {}, onChangePassword = () => {} }) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const styles = getStyles(theme);
  const userProfile = profileStore.profile;

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const fileIsValid = file ? validateImageFile(file) : false;

    if (!fileIsValid) {
      snackStore.showError("Invalid file type or size");
      return;
    }

    if (fileIsValid && file) {
      const processedBlob = await processProfileImage(file);
      const processedFile = new File([processedBlob], file.name, { type: file.type });
      await profileStore.uploadAvatar(processedFile);
    }
  };

  return (
    <Stack sx={styles.section}>
      <Box component="label" sx={styles.avatar as SxProps<Theme>}>
        <Avatar
          title={t("Update profile picture")}
          alt={`${userProfile.firstName} ${userProfile.lastName}`}
          src={getAvatarUrl(userProfile.avatarUrl)}
          sx={styles.avatar.photo}
          slotProps={{
            img: {
              onError: (e: SyntheticEvent) => {
                console.error("Avatar image failed to load:", e);
                // (e.target as HTMLImageElement).src = "/images/profile.png";
              },
            },
          }}
        >
          <AccountCircleIcon sx={{ width: "6rem", height: "6rem" }} />
        </Avatar>
        <input
          ref={fileRef}
          hidden
          type="file"
          accept="image/*"
          onChange={handleAvatarChange}
          style={styles.avatar.input as CSSProperties}
        />
      </Box>
      <Stack direction="column" flex="1">
        <Stack sx={styles.salutationAndName}>
          <FormControl sx={styles.salutation}>
            <InputLabel id="demo-controlled-open-select-label">{t("Salutation")}</InputLabel>
            <Select
              labelId="salutation-label"
              value={userProfile.salutation.toLowerCase()}
              onChange={(e) => {
                userProfile.salutation = e.target.value.toLowerCase();
              }}
              label={t("Salutation")}
              error={profileStore.showErrors && profileStore.profile.salutation === ""}
            >
              <MenuItem value="mr">{t("Mr.")}</MenuItem>
              <MenuItem value="ms">{t("Ms.")}</MenuItem>
              <MenuItem value="mx">{t("Mx.")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={t("First Name")}
            value={userProfile.firstName}
            error={profileStore.showErrors && profileStore.profile.firstName.length < 2}
            onChange={(e) => {
              userProfile.firstName = e.target.value;
            }}
            sx={styles.firstName}
          />
          <TextField
            fullWidth
            label={t("Last Name")}
            error={profileStore.showErrors && profileStore.profile.lastName.length < 2}
            value={userProfile.lastName}
            onChange={(e) => {
              userProfile.lastName = e.target.value;
            }}
            sx={styles.lastName}
          />
        </Stack>
        <Stack sx={styles.twoFactorAndPassword}>
          <Stack direction="row" sx={styles.twoFactorAuth}>
            <FormControlLabel
              control={<Switch onChange={onSwitch2FA} />}
              label={t("Two-factor authentication")}
              checked={userProfile.twoFactorAuth === "yes"}
            />
            <Tooltip
              title={`${t(
                "Two-Factor Authentication greatly enhances security by requiring both something you know (like a password) and something you have (like a phone), making it much harder for attackers to gain access",
              )}. ${t("To help keep your account secure, please enable two-factor authentication")}.`}
            >
              <InfoOutline sx={{ width: 20, height: 20, color: "var(--color-info)", cursor: "pointer" }} />
            </Tooltip>
          </Stack>
          <Button sx={styles.changePassword} onClick={onChangePassword}>
            {t("Change Password")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
});
